import React, { useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './components/Notification';
import { NotificationInboxProvider } from './context/NotificationInboxContext';
import { StoreCartProvider } from './context/StoreCartContext';
import { WalletProvider } from './context/WalletContext';
import { AdSenseProvider } from './context/AdSenseContext';
import Navbar from './components/Navbar';
import LandingRedirect from './components/LandingRedirect';
import ProtectedRoute from './components/ProtectedRoute';
import CupPage from './pages/CupPage';
import MatchDetail from './pages/MatchDetail';
import Admin from './pages/Admin';
import AdminMarketControl from './pages/AdminMarketControl';
import SuperAdmin from './pages/SuperAdmin';
import Unauthorized from './pages/Unauthorized';
import Leaderboard from './pages/Leaderboard';
import Streaks from './pages/Streaks';
import Jackpot from './pages/Jackpot';
import Referral from './pages/Referral';
import Profile from './pages/Profile';
import WalletPage from './pages/Wallet';
import Notifications from './pages/Notifications';
import AuthPage from './pages/AuthPage';
import Store from './pages/Store';
import StoreProduct from './pages/StoreProduct';
import StoreCart from './pages/StoreCart';
import StoreCheckout from './pages/StoreCheckout';
import StoreOrders from './pages/StoreOrders';
import StoreOrderDetail from './pages/StoreOrderDetail';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ResponsibleGaming from './pages/ResponsibleGaming';
import Cookies from './pages/Cookies';
import Footer from './components/Footer';
import WalletAccountSync from './components/WalletAccountSync';
import WalletLimitModalHost from './components/WalletLimitModalHost';
import ReferralCapture from './components/ReferralCapture';
import SeoHead from './components/SeoHead';
import './App.css';
import { syncChainConfigFromServer } from './utils/syncChainConfig';

// Component to handle scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  useEffect(() => {
    syncChainConfigFromServer().catch((e) => {
              console.warn('[WE R GAME] Could not sync chain config from API:', e?.message || e);
    });
  }, []);

  const appTree = (
    <AuthProvider>
      <WalletProvider>
        <NotificationProvider>
          <NotificationInboxProvider>
          <AdSenseProvider>
          <StoreCartProvider>
          <Router>
          <ScrollToTop />
          <SeoHead />
          <WalletAccountSync />
          <WalletLimitModalHost />
          <ReferralCapture />
          <div className="App min-h-screen bg-gray-50 dark:bg-gray-900">
            <Navbar />
            <Routes>
              {/* Public */}
              <Route path="/" element={<LandingRedirect />} />
              <Route path="/cup/:cupSlug" element={<CupPage />} />
              <Route path="/match/:matchId/:type" element={<MatchDetail />} />
              <Route path="/poll/:pollId/:type" element={<MatchDetail />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/streaks" element={<Streaks />} />
              <Route path="/jackpot" element={<Jackpot />} />
              <Route path="/store" element={<Store />} />
              <Route path="/store/product/:slug" element={<StoreProduct />} />
              <Route path="/store/cart" element={<StoreCart />} />
              <Route path="/login" element={<AuthPage />} />
              <Route
                path="/referral"
                element={
                  <ProtectedRoute>
                    <Referral />
                  </ProtectedRoute>
                }
              />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Authenticated users */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wallet"
                element={
                  <ProtectedRoute>
                    <WalletPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store/checkout"
                element={
                  <ProtectedRoute>
                    <StoreCheckout />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store/orders"
                element={
                  <ProtectedRoute>
                    <StoreOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store/orders/:id"
                element={
                  <ProtectedRoute>
                    <StoreOrderDetail />
                  </ProtectedRoute>
                }
              />

              {/* Admin + SuperAdmin (matches backend isAdmin) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/market/:kind/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'superAdmin']}>
                    <AdminMarketControl />
                  </ProtectedRoute>
                }
              />

              {/* SuperAdmin only (matches backend isSuperAdmin) */}
              <Route
                path="/superadmin"
                element={
                  <ProtectedRoute allowedRoles={['superAdmin']}>
                    <SuperAdmin />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Footer />
          </div>
        </Router>
          </StoreCartProvider>
          </AdSenseProvider>
          </NotificationInboxProvider>
        </NotificationProvider>
      </WalletProvider>
    </AuthProvider>
  );

  if (googleClientId) {
    return <GoogleOAuthProvider clientId={googleClientId}>{appTree}</GoogleOAuthProvider>;
  }
  return appTree;
}

export default App;
